import { ChangeEvent, KeyboardEvent, RefObject, SyntheticEvent, useCallback, useEffect, useRef, useState } from "react"
import Configs from "../../Configs"
import useAppContext from "../../hooks/useAppContext"
import TicketTemplateService, { ITicketTemplate } from "../../services/TicketTemplateService"
import ContentHeader from "../../components/ContentHeader"
import ContentBody from "../../components/ContentBody"
import { Accordion, Alert, Button, Card, Col, Container, Form, InputGroup, Modal, Row, Spinner, Tab, Table, Tabs } from "react-bootstrap"
import Pagination from "../../components/Pagination"
import DeleteConfirmation from "../../components/DeleteConfirmation"
import Utils from "../../utils/Utils"
import HtmlToPrint from "../../components/HtmlToPrint"
import { useReactToPrint } from "react-to-print"
import useAuthContext from "../../hooks/useAuthContext"
import Permissions from "../../constants/Permissions"
import { TicketTypes } from "../../constants/TicketTypes"
import { PrintMethods } from "../../constants/PrintMethods"

function TicketTemplates() {
    const { hasPermission } = useAuthContext()
    const { state } = useAppContext()

    const [tableData, setTableData] = useState({
        data: new Array<ITicketTemplate>(),
        hasNext: false,
        hasPrevious: false,
        pageIndex: 1,
        pageSize: Configs.DEFAULT_PAGE_SIZE,
        totalPages: 0,
        totalRecords: 0
    })

    const initFormData: ITicketTemplate = {
        id: null,
        name: "",
        paperSizeWidth: "",
        paperSizeHeight: "",
        htmlContent: "",
        styleSheet: "",
        bgImage: "",
        logo: "",
        printMethod: "",
        type: "",
        isUsed: false
    }

    const [message, setMessage] = useState("")
    const [messageFormModal, setMessageFormModal] = useState("")
    const [validatedFormModal, setValidatedFormModal] = useState(false)
    const [formData, setFormData] = useState(initFormData)
    const [currentPageIndex, setCurrentPageIndex] = useState(1)
    const [deletedItem, setDeletedItem] = useState({
        id: 0,
        name: ""
    })

    const keywordRef = useRef<HTMLInputElement>(null)
    const formRef = useRef<HTMLFormElement>(null)
    // thumbnail
    const [logo, setLogo] = useState(null as any)
    const [bgImage, setBGImage] = useState(null as any)

    const handleLogoChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) {
            setLogo(e.target.files[0])
        } else {
            setLogo(null)
        }
    }

    const handleBGImageChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) {
            setBGImage(e.target.files[0])
        } else {
            setBGImage(null)
        }
    }

    const FilterFn = async (pageIndex: number) => {
        if (state.currentProviderId) {
            let pageSize = Configs.DEFAULT_PAGE_SIZE
            let keyword = keywordRef.current?.value || ""

            const res = await TicketTemplateService.filter(state.currentProviderId, pageIndex, pageSize, keyword)

            if (res?.isSuccess) {
                setTableData(res.data)
            } else {
                console.log(res?.message)
            }

            setCurrentPageIndex(pageIndex)
        }
    }

    useEffect(() => {
        keywordRef.current?.focus()

        FilterFn(1)
    }, [state.currentProviderId])

    const handleSearch = () => {
        FilterFn(1)
    }

    const handleSearchInputKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            FilterFn(1)
        }
    }

    const handlePageChange = (pageNumber: number) => {
        FilterFn(pageNumber)
    }

    const handleFormControlChange = (e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value })
    }

    const handleAddNew = () => {
        setFormData(initFormData)

        handleShowModal()
    }

    const handleEdit = async (id: number | null) => {
        if (state.currentProviderId && id) {
            const res = await TicketTemplateService.get(state.currentProviderId, id)

            if (res?.isSuccess) {
                setFormData(res.data)

                handleShowModal()
            } else {
                console.log(res?.message)
            }
        }
    }

    const [processing, setProcessing] = useState<boolean>(false)
    const [isClose, setIsClose] = useState<boolean>(false)
    const handleSave = async (e: SyntheticEvent, isClose: boolean) => {
        e.preventDefault()
        setIsClose(isClose)

        if (state.currentProviderId && !processing) {
            setProcessing(true)
            if (formRef.current?.checkValidity()) {
                var frmData = new FormData()
                if (logo !== null) {
                    frmData.append("Logo", logo)
                }
                if (bgImage !== null) {
                    frmData.append("BGImage", bgImage)
                }
                frmData.append("Name", formData.name.toString())
                frmData.append("PaperSizeWidth", formData.paperSizeWidth.toString())
                frmData.append("PaperSizeHeight", formData.paperSizeHeight.toString())
                frmData.append("HtmlContent", formData.htmlContent.toString())
                frmData.append("StyleSheet", formData.styleSheet.toString())
                frmData.append("PrintMethod", formData.printMethod.toString())
                frmData.append("Type", formData.type.toString())

                if (formData.id && formData.id > 0) {
                    const res = await TicketTemplateService.update(state.currentProviderId, formData.id, frmData)

                    if (res?.isSuccess) {
                        FilterFn(currentPageIndex)
                        setMessage(res.message)
                        if (isClose) {
                            handleCloseModal()
                        } else {
                            setBGImage(null)
                            setLogo(null)
                            setFormData(res.data)
                        }
                    } else {
                        setMessageFormModal(res?.message)
                    }
                } else {
                    const res = await TicketTemplateService.create(state.currentProviderId, frmData)

                    if (res?.isSuccess) {
                        FilterFn(1)
                        setMessage(res.message)
                        if (isClose) {
                            handleCloseModal()
                        } else {
                            setBGImage(null)
                            setLogo(null)
                            setFormData(res.data)
                        }
                    } else {
                        setMessageFormModal(res?.message)
                    }
                }
            } else {
                setValidatedFormModal(true)
            }
            setProcessing(false)
        }
    }

    const [showModal, setShowModal] = useState(false)
    const handleCloseModal = () => {
        setShowModal(false)
        setValidatedFormModal(false)
        setMessageFormModal("")
    }
    const handleShowModal = () => {
        setShowModal(true)
        setMessage("")
    }

    const modalTitle = formData.id ? "Edit Ticket Template" : "New Ticket Template"

    //Delete
    const [displayConfirmationModal, setDisplayConfirmationModal] = useState(false)

    const hideConfirmationModal = () => {
        setDeletedItem({
            id: 0,
            name: ""
        })

        setDisplayConfirmationModal(false)
    }

    const showDeleteConfirmation = (id: number | null, name: string) => {
        setDeletedItem({
            id: id || 0,
            name: name
        })

        setDisplayConfirmationModal(true)
        setMessage("")
    }

    const handleDelete = async () => {
        if (state.currentProviderId && deletedItem.id) {
            const res = await TicketTemplateService.delete(state.currentProviderId, deletedItem.id)

            if (res?.isSuccess) {
                FilterFn(currentPageIndex)
            }

            hideConfirmationModal()
            setMessage(res.message)
        }
    }

    //Print
    const [isPrinting, setIsPrinting] = useState(false)
    const printRef = useRef(null)
    const onBeforeGetContentResolve = useRef<(() => void) | null>(null)
    const [templateId, setTemplateId] = useState<number | null>(null)
    const [templateContentToPrint, setTemplateContentToPrint] = useState<ITicketTemplate | null>(null)

    const loadTemplateContent = async () => {
        if (state.currentProviderId && templateId) {
            const setting = await TicketTemplateService.get(state.currentProviderId, templateId)

            if (setting?.isSuccess && setting.data) {
                const ticketTemplate: ITicketTemplate = setting.data
                var htmlContent = ticketTemplate.htmlContent
                    .replaceAll("{code}", "12345678")
                    .replaceAll("{qrcode}", "data:image/pngbase64,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")
                    .replaceAll("{price}", Utils.formatNumber(50000))
                    .replaceAll("{currency}", "VND")
                    .replaceAll("{timeLimit}", "8")
                    .replaceAll("{placeName}", "Place Name")
                    .replaceAll("{createdOn}", Utils.dateToString(new Date(), `${Configs.DATE_FORMAT} ${Configs.TIME_FORMAT}`))

                setTemplateContentToPrint({ ...ticketTemplate, htmlContent: htmlContent })
            } else {
                setTemplateContentToPrint(null)
            }
        } else {
            setTemplateContentToPrint(null)
        }
    }

    const handleOnBeforeGetContent = useCallback(() => {
        setIsPrinting(true)

        return new Promise<void>((resolve) => {
            onBeforeGetContentResolve.current = resolve
            setIsPrinting(false)
            setTemplateId(null)
            resolve()
        })
    }, [isPrinting, templateContentToPrint])

    const handlePrintHTML = useReactToPrint({
        content: () => printRef?.current,
        onBeforeGetContent: handleOnBeforeGetContent,
        onAfterPrint: () => {
            onBeforeGetContentResolve.current = null
            setTemplateContentToPrint(null)
        },
        removeAfterPrint: true
    })

    useEffect(() => {
        if (templateContentToPrint && typeof onBeforeGetContentResolve.current === "function") {
            onBeforeGetContentResolve.current()
        }
    }, [onBeforeGetContentResolve.current, templateContentToPrint])

    useEffect(() => {
        loadTemplateContent()
    }, [templateId])

    useEffect(() => {
        if (templateContentToPrint) {
            handlePrintHTML()
        }
    }, [templateContentToPrint])

    const handlePrint = (id: number | null, printMethod: string) => {
        if (state.currentProviderId && id) {
            if (printMethod === "HTML") {
                setTemplateId(id)
            } else {
                TicketTemplateService.generatePDF(state.currentProviderId, id)
            }
        }
    }

    const handleIsUsedSwitch = async (id: number | null) => {
        if (state.currentProviderId && id) {
            const res = await TicketTemplateService.changeIsUsed(state.currentProviderId, id)

            if (res?.isSuccess) {
                FilterFn(currentPageIndex)
            } else {
                console.log(res?.message)
            }
        }
    }

    return (
        <>
            <ContentHeader title="Ticket Templates" />

            <ContentBody>
                <Container fluid>
                    <Row>
                        <Col xs={12}>
                            <Card>
                                <Card.Header>
                                    {hasPermission(Permissions.TicketTemplates.Create) &&
                                        <Button variant="info" size="sm" onClick={handleAddNew}>
                                            <i className="fas fa-plus"></i> Add new
                                        </Button>
                                    }

                                    <div className="card-tools">
                                        <InputGroup size="sm">
                                            <Form.Control
                                                type="search"
                                                placeholder="Search"
                                                ref={keywordRef as RefObject<HTMLInputElement>}
                                                onKeyUp={handleSearchInputKeyPress}
                                            />
                                            <Button variant="info" size="sm" onClick={handleSearch}>
                                                <i className="fas fa-search"></i>
                                            </Button>
                                        </InputGroup>
                                    </div>
                                </Card.Header>
                                <Card.Body>
                                    {message !== "" && <Alert variant="info" onClose={() => setMessage("")} dismissible>{message}</Alert>}
                                    <Table responsive striped bordered hover>
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Print Method</th>
                                                <th>Type</th>
                                                <th>Is Used</th>
                                                <th style={{ textAlign: "center", width: "80px" }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                tableData.data.map(item =>
                                                    <tr key={item.id}>
                                                        <td>{item.name}</td>
                                                        <td>{item.printMethod}</td>
                                                        <td>{item.type}</td>
                                                        <td style={{ textAlign: "center" }}>
                                                            <Form.Check type="switch" id={"ticket_template_isused_" + item.id} checked={item.isUsed} onChange={() => handleIsUsedSwitch(item.id)} disabled={!hasPermission(Permissions.TicketTemplates.Edit)} />
                                                        </td>
                                                        <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                                                            {hasPermission(Permissions.TicketTemplates.View) &&
                                                                <Button variant="secondary" className="mr-1" size="sm" onClick={() => handlePrint(item.id, item.printMethod)}><i className="fas fa-print"></i> Print</Button>
                                                            }
                                                            {hasPermission(Permissions.TicketTemplates.Edit) &&
                                                                <Button variant="warning" className="mr-1" size="sm" onClick={() => handleEdit(item.id)}><i className="fas fa-pencil-alt"></i> Edit</Button>
                                                            }
                                                            {hasPermission(Permissions.TicketTemplates.Delete) &&
                                                                <Button variant="danger" size="sm" onClick={() => showDeleteConfirmation(item.id, item.name)}><i className="fas fa-trash-alt"></i> Delete</Button>
                                                            }
                                                        </td>
                                                    </tr>
                                                )
                                            }
                                        </tbody>
                                    </Table>
                                </Card.Body>
                                <Card.Footer>
                                    <Pagination hasNext={tableData.hasNext} hasPrevious={tableData.hasPrevious} pageIndex={tableData.pageIndex} pageSize={tableData.pageSize} totalPages={tableData.totalPages} totalRecords={tableData.totalRecords} handlePageChange={handlePageChange} />
                                </Card.Footer>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </ContentBody>

            <Modal show={showModal} onHide={handleCloseModal} backdrop="static" keyboard={false} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>{modalTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form noValidate validated={validatedFormModal} ref={formRef as RefObject<HTMLFormElement>}>
                        <Form.Group className="mb-3" controlId="name">
                            <Form.Label>Name</Form.Label>
                            <Form.Control type="text" name="name" required value={formData.name} onChange={(e) => handleFormControlChange(e as any)} />
                            <Form.Control.Feedback type="invalid">Name is required.</Form.Control.Feedback>
                        </Form.Group>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3" controlId="printMethod">
                                    <Form.Label>Print Method</Form.Label>
                                    <Form.Select name="printMethod" required value={formData.printMethod || ""} onChange={(e) => handleFormControlChange(e as any)}>
                                        <option value=""></option>
                                        <option value={PrintMethods.HTML}>HTML</option>
                                        <option value={PrintMethods.PDF}>PDF</option>
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">Select a Print Method.</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3" controlId="type">
                                    <Form.Label>Type</Form.Label>
                                    <Form.Select name="type" value={formData.type || ""} onChange={(e) => handleFormControlChange(e as any)}>
                                        <option value=""></option>
                                        <option value={TicketTypes.APP_VOUCHER}>App Voucher</option>
                                        <option value={TicketTypes.EVENT_TICKET}>Event Ticket</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3" controlId="paperSizeWidth">
                            <Form.Label>Paper Size</Form.Label>
                            <InputGroup>
                                <InputGroup.Text>Width</InputGroup.Text>
                                <Form.Control type="text" name="paperSizeWidth" required value={formData.paperSizeWidth || ""} onChange={(e) => handleFormControlChange(e as any)} />
                                <InputGroup.Text>Height</InputGroup.Text>
                                <Form.Control style={{ borderTopRightRadius: "0.375rem", borderBottomRightRadius: "0.375rem" }} type="text" name="paperSizeHeight" required value={formData.paperSizeHeight || ""} onChange={(e) => handleFormControlChange(e as any)} />
                                <Form.Control.Feedback type="invalid">Paper Size Width and Height is required.</Form.Control.Feedback>
                            </InputGroup>
                        </Form.Group>
                        <Tabs defaultActiveKey="htmlContent">
                            <Tab eventKey="htmlContent" title="Html Content">
                                <Form.Group className="pt-1" controlId="htmlContent">
                                    <Form.Control style={{ border: "none" }} as="textarea" rows={20} name="htmlContent" required value={formData.htmlContent || ""} onChange={(e) => handleFormControlChange(e as any)} />
                                    <Form.Control.Feedback type="invalid">htmlContent is required.</Form.Control.Feedback>
                                </Form.Group>
                            </Tab>
                            <Tab eventKey="styleSheet" title="Style Sheet">
                                <Form.Group className="pt-1" controlId="styleSheet">
                                    <Form.Control style={{ border: "none" }} as="textarea" rows={20} name="styleSheet" value={formData.styleSheet || ""} onChange={(e) => handleFormControlChange(e as any)} />
                                </Form.Group>
                            </Tab>
                            <Tab eventKey="bgImageFile" title="Background Image">
                                <Form.Group controlId="bgImageFile">
                                    <Card className="m-0 border-0 shadow-0">
                                        {formData.bgImage && <Card.Img variant="top" src={`${Configs.API_BASE_URL}${formData.bgImage}`} style={{ width: "inherit", maxWidth: "300px", margin: "auto" }} />}
                                        <Card.Body className="p-2">
                                            <Form.Control type="file" onChange={handleBGImageChange} accept={Configs.IMAGE_EXTENSION} />
                                        </Card.Body>
                                    </Card>
                                </Form.Group>
                            </Tab>
                            <Tab eventKey="logo" title="Logo">
                                <Form.Group controlId="logoFile">
                                    <Card className="m-0 border-0 shadow-0">
                                        {formData.logo && <Card.Img variant="top" src={`${Configs.API_BASE_URL}${formData.logo}`} style={{ width: "inherit", maxWidth: "300px", margin: "auto" }} />}
                                        <Card.Body className="p-2">
                                            <Form.Control type="file" onChange={handleLogoChange} accept={Configs.IMAGE_EXTENSION} />
                                        </Card.Body>
                                    </Card>
                                </Form.Group>
                            </Tab>
                        </Tabs>
                    </Form>
                    <Accordion>
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>App Vourcher Template Variables</Accordion.Header>
                            <Accordion.Body>
                                {"{code}, <img src=\"{qrcode}\"/>, {price}, {currency}, {timeLimit}, {placeName}, {logo}, {bgImage}, {createdOn}"}
                            </Accordion.Body>
                        </Accordion.Item>
                        <Accordion.Item eventKey="1">
                            <Accordion.Header>Event Ticket Template Variables</Accordion.Header>
                            <Accordion.Body>
                                {"{code}, <img src=\"{qrcode}\"/>, {price}, {currency}, {eventId}, {eventName}, {<img src=\"{thumbnail}\"/>}, {eventDate}, {eventStartTime}, {eventEndTime}, {tel}, {email}, {website}, {<img src=\"{websiteQRCode}\"/>}, {address}, {ticketClass}, {seat}, {eventTicketId}, {createdOn}, {logo}, {bgImage}"}
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                    {messageFormModal !== "" && <Alert variant="danger" onClose={() => setMessageFormModal("")} dismissible>{messageFormModal}</Alert>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={handleCloseModal}><i className="fas fa-times"></i> Cancel</Button>
                    <Button variant="info" onClick={(e) => handleSave(e, false)} disabled={processing}>
                        {processing && !isClose ? <><Spinner animation="border" size="sm" /> Saving...</> : <><i className="fas fa-save"></i> Save and continue editing</>}
                    </Button>
                    <Button variant="info" onClick={(e) => handleSave(e, true)} disabled={processing}>
                        {processing && isClose ? <><Spinner animation="border" size="sm" /> Saving...</> : <><i className="fas fa-save"></i> Save</>}
                    </Button>
                </Modal.Footer>
            </Modal>

            <DeleteConfirmation show={displayConfirmationModal} onConfirm={handleDelete} onHide={hideConfirmationModal} id={deletedItem.id} name={deletedItem.name} />

            <HtmlToPrint
                htmlContent={templateContentToPrint?.htmlContent || ""}
                styleSheet={templateContentToPrint?.styleSheet || ""}
                paperSizeWidth={templateContentToPrint?.paperSizeWidth || ""}
                paperSizeHeight={templateContentToPrint?.paperSizeHeight || ""}
                ref={printRef}
            ></HtmlToPrint>
        </>
    )
}

export default TicketTemplates